import {ChangeDetectionStrategy, Component, OnInit, inject} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Localization} from '../../../../projects/common/src/lib/interface/localization.extension';
import {AccountApiService} from '../../common/services/api/routes/account-api.service';
import {AuthGuardService} from '../../common/services/auth-guard.service';
import {Logger} from '../../common/utils/logger';

type Phase = 'loading' | 'ask' | 'working' | 'done' | 'invalid' | 'error';

/**
 * /unsubscribe/:token, the link at the foot of an outreach email (Admin ->
 * Growth): takes that business off our list for good.
 *
 * Open to anyone holding the link; the token names that one email. Nothing
 * happens until the button is pressed: mail scanners open links. Mail clients'
 * own one-click unsubscribe posts to the API directly.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'app-unsubscribe-page',
  templateUrl: 'unsubscribe.page.html',
})
export class UnsubscribePage extends Localization implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly accountApi = inject(AccountApiService);
  private readonly authGuard = inject(AuthGuardService);

  phase: Phase = 'loading';
  /** The business the email went to. */
  name = '';

  private token = '';

  ngOnInit() {
    // No auth guard runs here, and it is the guard that lifts the start-up splash.
    this.authGuard.loader.set(false);
    this.authGuard.loader$.next(false);
    this.token = this.route.snapshot.paramMap.get('token') || '';
    if (!/^[a-f0-9]{32}$/.test(this.token)) {
      this.phase = 'invalid';
      return;
    }
    this.accountApi.outreachUnsubscribeInfo(this.token).then(info => {
      this.name = info?.name || '';
      this.phase = !info?.found ? 'invalid' : info.done ? 'done' : 'ask';
    }).catch(err => {
      Logger.warn('Unsubscribe lookup failed', err?.msg?.errorCode);
      this.phase = 'error';
    });
  }

  unsubscribe() {
    if (this.phase === 'working') {
      return;
    }
    this.phase = 'working';
    this.accountApi.outreachUnsubscribe(this.token).then(result => {
      this.name = result?.name || this.name;
      this.phase = result?.found ? 'done' : 'invalid';
    }).catch(err => {
      Logger.warn('Unsubscribe failed', err?.msg?.errorCode);
      this.phase = 'error';
    });
  }
}
